
import {
  defineComponent, onMounted, ref, computed, reactive, nextTick,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';
// eslint-disable-next-line import/no-cycle
import { useMainStore } from '@/store/mainStore';
// eslint-disable-next-line import/no-cycle
import { useDriveStore } from '../store/driveStore';
import AppSkeletonCaseView from '@/components/skeletons/AppSkeletonCaseView.vue';
import DriveInput from '../components/UI/DriveInput.vue';
import AppButton from '@/components/UI/AppButton.vue';
import {
  CASE_CREATION_POP_UP_SWITCHER, HIDE_BORDER, MEDIA_TYPES, PHOTO_VIEW_TYPE, SHARE_TYPES, UPLOAD_PLACES,
} from '@/types/enums';
import AppLabelInput from '@/components/UI/AppLabelInput.vue';
import DriveUiWrapper from '../components/UI/DriveUIWrapper.vue';
// eslint-disable-next-line import/no-cycle
import MonitoringService from '@/services/MonitoringService';
import {
  ICaseFilesManage,
  IFileUpload,
  ILabelInput, IMedia, ISaveCasePayload, ITemporaryFile,
} from '@/types/interfaces';
import AppHeader from '@/components/AppHeader.vue';
import UploadFilesPopUp from '@/components/BottomPopUp/PopUpTypes/Upload/UploadFilesPopUp.vue';
import UploadPhotosPopUp from '@/components/BottomPopUp/PopUpTypes/Upload/UploadPhotosPopUp.vue';
import DriveAddFiles from '../components/DriveAddFiles.vue';
import DriveAddPhotos from '../components/DriveAddPhotos.vue';
import DriveCreateCaseFiles from '../components/UI/DriveCreateCaseFiles.vue';
import { lowResUrlByViewType } from '@/helpers/lowResPhotoUrl';
// eslint-disable-next-line import/no-cycle
import UploadService from '@/services/UploadService';
import UseUpload from '@/composables/UseUpload';
import DriveCreateCaseCancelPopUp from '../components/UI/DriveCreateCaseCancelPopUp.vue';
import DriveDesktopAddFiles from '@/modules/drive/components/desktop/cases/DriveDesktopAddFiles.vue';
import AppVoiceToText from '@/components/AppVoiceToText.vue';
import DriveDesktopAddPhotos from '@/modules/drive/components/desktop/cases/DriveDesktopAddPhotos.vue';
import DesktopUploadProgress from '@/modules/drive/components/desktop/UI/DesktopUploadProgress.vue';
import { blobToUrl, getVideoThumbnail } from '@/helpers/videoThumbnail';

export default defineComponent({
  components: {
    DriveDesktopAddPhotos,
    DriveInput,
    DriveCreateCaseFiles,
    AppButton,
    AppLabelInput,
    DriveUiWrapper,
    AppHeader,
    AppSkeletonCaseView,
    UploadFilesPopUp,
    UploadPhotosPopUp,
    DriveAddFiles,
    DriveAddPhotos,
    DriveCreateCaseCancelPopUp,
    DriveDesktopAddFiles,
    DesktopUploadProgress,
    // AppVoiceToText,
    // AppAlert,
  },
  props: {
    photos: { type: Array as () => IFileUpload[], required: false },
  },
  emits: ['close', 'create'],
  setup(props, { emit }) {
    const mainStore = useMainStore();
    const driveStore = useDriveStore();
    const route = useRoute();
    const router = useRouter();

    const popUpManager = ref<CASE_CREATION_POP_UP_SWITCHER>(CASE_CREATION_POP_UP_SWITCHER.NONE);

    const createCasePayload = reactive<ISaveCasePayload>({
      title: '',
      description: '',
      patientId: '',
      patientName: '',
      fileIds: [],
      labelIds: [],
    });

    function setVoiceToDescription(v: string) {
      createCasePayload.description = v;
    }

    const validation = reactive({
      title: '',
      patientName: '',
      patientId: '',
      description: '',
    });

    const mediaSkeletons = reactive({
      photos: 0,
      files: 0,
    });

    const previewFiles = ref<ITemporaryFile[]>([]);
    const previewPhotos = ref<ITemporaryFile[]>([]);

    const uploadPhotos = ref<{ id: string, file: File, isProps: boolean }[]>([]);
    const uploadFiles = ref<{ id: string, file: File, isProps: boolean }[]>([]);

    const driveFiles = ref<IMedia[]>([]);
    const drivePhotos = ref<IMedia[]>([]);

    const labels = ref<ILabelInput[]>([]);

    const isCaseCreating = ref(false);

    const { onUploadUpdate } = UseUpload();

    const photosPopUpPos = ref({});
    const filesPopUpPos = ref({});
    const poster = ref('');

    function addPhotos(pos) {
      popUpManager.value = CASE_CREATION_POP_UP_SWITCHER.UPLOAD_PHOTOS;
      photosPopUpPos.value = pos;
    }
    function addFiles(pos) {
      popUpManager.value = CASE_CREATION_POP_UP_SWITCHER.UPLOAD_FILES;
      filesPopUpPos.value = pos;
    }

    const isDoneDisabled = computed(() => (
      validation.title || validation.description
      || validation.patientName || validation.patientId || !createCasePayload.title
    ));

    function onUploadMedia(files: FileList, isPhoto: boolean, isProps = false) {
      for (let i = 0; i < files.length; i += 1) {
        const id = uuidv4() as string;

        const upload = { id, file: files[i], isProps };
        const preview = {
          url: URL.createObjectURL(files[i]),
          id,
          name: files[i].name,
          isTemporary: true,
          isProps,
          isVideo: false,
        };
        if (files[i].type.includes('video')) {
          getVideoThumbnail(files[i], 1.0).then((thumbnailBlob) => {
            poster.value = blobToUrl(thumbnailBlob);
            preview.url = poster.value;
            preview.isVideo = true;
            uploadPhotos.value.push(upload);
            previewPhotos.value.push(preview);
          });
        } else if (isPhoto) {
          uploadPhotos.value.push(upload);
          previewPhotos.value.push(preview);
        } else {
          uploadFiles.value.push(upload);
          previewFiles.value.push(preview);
        }
      }

      popUpManager.value = CASE_CREATION_POP_UP_SWITCHER.NONE;
    }

    function removeUploadedFile(tempFile: ITemporaryFile, isPhoto: boolean) {
      const unSelect = () => {
        const mediaList = isPhoto ? driveStore.photos.data : driveStore.files;
        const currentItem = mediaList.find((item) => item.id === tempFile.id);
        if (currentItem) {
          currentItem.selected = false;
        }
      };

      const preview = isPhoto ? previewPhotos : previewFiles;
      const upload = isPhoto ? uploadPhotos : uploadFiles;
      const drive = isPhoto ? drivePhotos : driveFiles;

      preview.value = preview.value.filter((item) => item.id !== tempFile.id);

      if (tempFile.isTemporary) {
        upload.value = upload.value.filter((item) => item.id !== tempFile.id);
      } else {
        unSelect();
        drive.value = drive.value.filter((item) => item.id !== tempFile.id);
      }
    }

    function resolvePhoto(photo: IMedia) {
      if (photo.type === MEDIA_TYPES.PHOTO) {
        return lowResUrlByViewType(photo, PHOTO_VIEW_TYPE.WHSML);
      }
      return process.env.VUE_APP_MEDIA_URL + photo.previewUrl;
    }

    function onSelectMedia(files: { selected: IMedia[], deselected: IMedia[] }, isPhoto: boolean) {
      const drive = isPhoto ? drivePhotos : driveFiles;
      const preview = isPhoto ? previewPhotos : previewFiles;

      drive.value = drive.value.filter((item) => files.selected.find((sel) => sel.id === item.id));
      preview.value = preview.value.filter((item) => {
        if (item.isTemporary) {
          return true;
        }
        return !!files.selected.find((sel) => sel.id === item.id);
      });

      const newSelected = files.selected.filter((sel) => !drive.value.find((media) => media.id === sel.id));
      drive.value.push(...newSelected);
      newSelected.forEach((newSel) => {
        preview.value.push({
          url: resolvePhoto(newSel),
          id: newSel.id,
          name: newSel.name,
          isTemporary: false,
          isProps: false,
        });
      });

      popUpManager.value = CASE_CREATION_POP_UP_SWITCHER.NONE;
    }

    async function attachFiles(caseId: string, fileIds: string[]) {
      const addFilePayload = {
        id: caseId,
        fileIds,
      } as ICaseFilesManage;

      return driveStore.addFilesToCase(addFilePayload);
    }

    async function createCase() {
      if (isDoneDisabled.value) {
        validation.title = 'This field is required';
        return;
      }

      isCaseCreating.value = true;

      await mainStore.tagsToLabelInput().then((resLabels) => {
        createCasePayload.labelIds = resLabels.map((lab) => lab.id);
      });

      driveStore.createCase(createCasePayload)
        // set creating Case flag to true
        .then(async (iCase) => {
          const driveMediaIds = [...driveFiles.value, ...drivePhotos.value].map((item) => item.id);
          if (driveMediaIds.length) {
            await attachFiles(iCase.id!, driveMediaIds);
          }

          const fileList = [...uploadPhotos.value, ...uploadFiles.value].filter((item) => !item.isProps).map((item) => item.file) as unknown as FileList;
          if (fileList.length || props.photos?.length) {
            const fileUploads = [...(props.photos || []), ...UploadService.toFileUploads(fileList)];

            mainStore.mediaUploading = true;
            UploadService.batchUploadTUS(fileUploads, onUploadUpdate, {
              location: UPLOAD_PLACES.CASE_PHOTO,
              id: iCase.id!,
            }, undefined, mainStore.recentInputLabels)
              .then((files) => {
                attachFiles(iCase.id!, files.map((file) => file.id)).then(() => {
                  // on finally set creating Case flag to false
                  router.replace({ query: { create: 1 } });
                }).finally(() => { mainStore.mediaUploading = false; });
              });
          }

          if (props.photos) {
            emit('create');
          }
          router.push(`/drive/case/my/${iCase.id}/view/?create=1`).then(() => {
            isCaseCreating.value = false;
          });
        })
        .catch((e) => {
          const errorData = e?.response?.data;
          if (errorData) {
            if (errorData.errorKey === 'CASE_NAME_EXISTS') {
              validation.title = 'Name is already taken';
            } else {
              validation.title = 'Something gone wrong';
            }
          } else {
            validation.title = 'Something gone wrong';
          }
          isCaseCreating.value = false;
        });
    }

    function validateCaseTitle(): void {
      if (!createCasePayload.title.trim()) {
        validation.title = 'This filed is required';
      } else if (createCasePayload.title.trim().length > 255) {
        validation.title = 'Case title must be max 255 characters long.';
      } else {
        validation.title = '';
      }
    }

    function validateCaseDescription(): void {
      if (createCasePayload.description!.length > 3000) {
        validation.description = 'Case description must be max 3000 characters long.';
      } else {
        validation.description = '';
      }
    }

    function validatePatientName(): void {
      if (createCasePayload.patientName!.length > 255) {
        validation.patientName = 'Case title must be max 255 characters long.';
      } else {
        validation.patientName = '';
      }
    }

    function validatePatientId(): void {
      if (createCasePayload.patientId!.length > 255) {
        validation.patientName = 'Case title must be max 255 characters long.';
      } else {
        validation.patientName = '';
      }
    }

    function updateTags() {
      labels.value = mainStore.inputCaseTags.map((tags) => ({
        name: tags.text,
        id: tags.id,
      } as ILabelInput));
    }

    function backAction() {
      if (router.options.history.state.back === '/drive/case/create' && driveStore.currentCase.id) {
        router.push(`/drive/case/create?caseId=${driveStore.currentCase!.id}`);
      } else if ((router.options.history.state.back as string).startsWith('/drive/case/create?') && driveStore.currentCase.id) {
        router.push(`${router.options.history.state.back}&caseId=${driveStore.currentCase!.id}`);
      } else {
        driveStore.resetCurrentCase();
        router.back();
      }
    }

    function uploadFilesToDrive() {
      const fileList = [...uploadFiles.value].map((item) => item.file) as unknown as FileList;
      if (fileList.length) {
        const fileUploads = UploadService.toFileUploads(fileList);
        UploadService.batchUploadTUS(fileUploads, onUploadUpdate, { location: UPLOAD_PLACES.MIXED });
      }

      const photoList = [...uploadPhotos.value].map((item) => item.file) as unknown as FileList;
      if (photoList.length) {
        const fileUploads = UploadService.toFileUploads(photoList);
        UploadService.batchUploadTUS(fileUploads, onUploadUpdate, { location: UPLOAD_PLACES.PHOTO });
      }

      backAction();
    }

    function validateBackAction() {
      if (props.photos) {
        emit('close');
        return;
      }
      if (uploadPhotos.value.length + uploadFiles.value.length) {
        popUpManager.value = CASE_CREATION_POP_UP_SWITCHER.CANCEL;
        return;
      }
      backAction();
    }

    function setFocusOnLabels() {
      const el = document.getElementById('labelsInput');
      if (el) {
        nextTick(() => {
          el.scrollIntoView();
        });
      }
    }

    onMounted(async () => {
      MonitoringService.sendPageMountedSpeed();

      driveStore.resetCurrentCase();
      driveStore.appPhotoViewMedia = undefined;
      mainStore.inputCaseTags = [];

      if (route.query.userFolder) {
        createCasePayload.folderId = mainStore.userSettings.accountSystemSettings.userCasesFolderId;
      } else if (route.query.folderId) {
        createCasePayload.folderId = route.query.folderId as string;
      }

      if (props.photos) {
        const fileList = (props.photos.map((item) => item.file)) as unknown as FileList;
        onUploadMedia(fileList, true, true);
      }
    });

    return {
      driveStore,
      isCaseCreating,
      isDoneDisabled,
      validatePatientName,
      validatePatientId,
      createCase,
      updateTags,
      validateCaseTitle,
      validateCaseDescription,
      backAction,
      HIDE_BORDER,
      SHARE_TYPES,

      mediaSkeletons,
      createCasePayload,
      validation,
      previewFiles,
      previewPhotos,
      popUpManager,
      CASE_CREATION_POP_UP_SWITCHER,
      onUploadMedia,
      onSelectMedia,
      removeUploadedFile,
      uploadFilesToDrive,
      validateBackAction,

      setVoiceToDescription,
      mainStore,
      setFocusOnLabels,
      addPhotos,
      addFiles,
      photosPopUpPos,
      filesPopUpPos,
    };
  },
});
